/**
 * Centralized Structured Logger for @flowstate/core
 *
 * A unified logging utility that provides:
 * - Environment-aware logging (dev vs production)
 * - Structured log format with timestamps
 * - Log level support (debug, info, warn, error, critical)
 * - Performance measurement utilities
 * - Namespace support for module-specific logging
 *
 * Usage:
 * ```ts
 * import { logger } from '../lib/logger';
 *
 * logger.debug('Detailed debugging info', { userId: 123 });
 * logger.info('General information', { action: 'user_login' });
 * logger.warn('Warning message', { reason: 'rate_limit' });
 * logger.error('Error occurred', { error: err, context: 'payment' });
 * logger.critical('Critical failure', { system: 'auth' });
 * ```
 */

/* eslint-disable no-console -- This is the logger implementation that wraps console */

type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'critical';

interface LogContext {
  [key: string]: unknown;
}

interface LogEntry {
  timestamp: string;
  level: LogLevel;
  message: string;
  namespace?: string;
  context?: LogContext;
}

export interface MyceliumLogReport {
  timestamp: string;
  level: Extract<LogLevel, 'warn' | 'error' | 'critical'>;
  message: string;
  namespace?: string;
  context?: LogContext;
}

type MyceliumLogReporter = (report: MyceliumLogReport) => Promise<void> | void;

let myceliumLogReporter: MyceliumLogReporter | null = null;
let myceliumReportQueue: Promise<void> = Promise.resolve();
let isReportingToMycelium = false;

const SENSITIVE_KEY_PATTERN =
  /password|passphrase|token|secret|authorization|cookie|session|jwt|bearer|api[_-]?key|apikey|access[_-]?key|accesskey|refresh[_-]?token/i;
const MAX_STRING_LENGTH = 500;
const MAX_OBJECT_DEPTH = 4;
const MAX_ARRAY_LENGTH = 20;

function isProductionEnvironment(): boolean {
  // Vite / Nitro / TanStack Start expose import.meta.env in ESM builds.
  const importMetaEnv =
    typeof import.meta !== 'undefined'
      ? (import.meta as unknown as { env?: Record<string, unknown> }).env
      : undefined;

  if (importMetaEnv) {
    if (importMetaEnv.PROD === true) return true;
    if (importMetaEnv.DEV === true) return false;
    if (importMetaEnv.NODE_ENV === 'production') return true;
  }

  // Node / Deno fallback.
  const nodeEnv =
    typeof globalThis !== 'undefined'
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (globalThis as any)?.process?.env?.NODE_ENV
      : undefined;

  return nodeEnv === 'production';
}

function truncateString(value: string): string {
  return value.length > MAX_STRING_LENGTH
    ? `${value.slice(0, MAX_STRING_LENGTH)}…`
    : value;
}

function sanitizeForMycelium(value: unknown, key?: string, depth = 0): unknown {
  if (key && SENSITIVE_KEY_PATTERN.test(key)) {
    return '[REDACTED]';
  }

  if (value == null || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return truncateString(value);
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: truncateString(value.message),
      stack: value.stack ? truncateString(value.stack) : undefined,
    };
  }

  if (depth >= MAX_OBJECT_DEPTH) {
    return '[TRUNCATED]';
  }

  if (Array.isArray(value)) {
    return value
      .slice(0, MAX_ARRAY_LENGTH)
      .map((item) => sanitizeForMycelium(item, undefined, depth + 1));
  }

  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([entryKey, entryValue]) => [
        entryKey,
        sanitizeForMycelium(entryValue, entryKey, depth + 1),
      ])
    );
  }

  return String(value);
}

function normalizeContext(context: unknown): LogContext | undefined {
  if (context === undefined || context === null) {
    return undefined;
  }

  if (context instanceof Error) {
    return { error: context };
  }

  if (typeof context !== 'object' || Array.isArray(context)) {
    return { detail: context };
  }

  return context as LogContext;
}

function getMyceliumControl(context?: LogContext): Record<string, unknown> | undefined {
  const candidate = context?.mycelium;
  return candidate && typeof candidate === 'object' ? (candidate as Record<string, unknown>) : undefined;
}

function shouldReportToMycelium(level: LogLevel, context?: LogContext): level is MyceliumLogReport['level'] {
  if (level === 'debug' || level === 'info') {
    return false;
  }

  const myceliumControl = getMyceliumControl(context);
  if (myceliumControl?.suppressMycelium === true) {
    return false;
  }

  return true;
}

export function setMyceliumLogReporter(reporter: MyceliumLogReporter | null): void {
  myceliumLogReporter = reporter;
  myceliumReportQueue = Promise.resolve();
  isReportingToMycelium = false;
}

export function getMyceliumLogReporter(): MyceliumLogReporter | null {
  return myceliumLogReporter;
}

class StructuredLogger {
  private namespace?: string;
  private isDevelopment: boolean;

  constructor(namespace?: string) {
    this.namespace = namespace;
    this.isDevelopment = !isProductionEnvironment();
  }

  /**
   * Formats and outputs a log entry
   */
  private log(level: LogLevel, message: string, context?: unknown): void {
    // Only log debug and info in development
    if (!this.isDevelopment && (level === 'debug' || level === 'info')) {
      return;
    }

    const normalizedContext = normalizeContext(context);

    const entry: LogEntry = {
      timestamp: new Date().toISOString(),
      level,
      message,
      ...(this.namespace && { namespace: this.namespace }),
      ...(normalizedContext && { context: normalizedContext }),
    };

    // Use appropriate console method based on level
    const consoleFn = this.getConsoleFn(level);

    // Format output based on environment
    if (this.isDevelopment) {
      const emoji = this.getLevelEmoji(level);
      const prefix = this.namespace ? `[${this.namespace}]` : '';
      consoleFn(`${emoji} ${prefix} ${message}`, normalizedContext || '');
    } else {
      // Production: JSON structured logging
      consoleFn(JSON.stringify(entry));
    }

    if (shouldReportToMycelium(level, normalizedContext)) {
      void this.reportToMycelium({
        timestamp: entry.timestamp,
        level,
        message,
        namespace: this.namespace,
        context: normalizedContext ? (sanitizeForMycelium(normalizedContext) as LogContext) : undefined,
      });
    }
  }

  private async reportToMycelium(report: MyceliumLogReport): Promise<void> {
    if (isReportingToMycelium || !myceliumLogReporter) {
      return;
    }

    myceliumReportQueue = myceliumReportQueue
      .then(async () => {
        if (!myceliumLogReporter) {
          return;
        }

        isReportingToMycelium = true;
        try {
          await myceliumLogReporter(report);
        } catch (error) {
          console.error('[mycelium-log-reporting] Failed to publish log event', {
            level: report.level,
            message: report.message,
            error: error instanceof Error ? error.message : String(error),
          });
        } finally {
          isReportingToMycelium = false;
        }
      })
      .catch(() => {
        // Reset the queue so future reports are not blocked by a prior rejection.
        myceliumReportQueue = Promise.resolve();
        isReportingToMycelium = false;
      });

    await myceliumReportQueue;
  }

  /**
   * Get appropriate console function for log level
   */
  private getConsoleFn(level: LogLevel): (...args: unknown[]) => void {
    switch (level) {
      case 'debug':
      case 'info':
        return console.info;
      case 'warn':
        return console.warn;
      case 'error':
      case 'critical':
        return console.error;
    }
  }

  /**
   * Get emoji for log level (dev mode only)
   */
  private getLevelEmoji(level: LogLevel): string {
    const emojis: Record<LogLevel, string> = {
      debug: '🔍',
      info: 'ℹ️',
      warn: '⚠️',
      error: '❌',
      critical: '🚨',
    };
    return emojis[level];
  }

  /**
   * Debug level - detailed information for debugging (dev only)
   */
  debug(message: string, context?: unknown): void {
    this.log('debug', message, context);
  }

  /**
   * Info level - general informational messages (dev only)
   */
  info(message: string, context?: unknown): void {
    this.log('info', message, context);
  }

  /**
   * Success level - success messages (dev only, uses info level)
   */
  success(message: string, context?: unknown): void {
    if (this.isDevelopment) {
      const prefix = this.namespace ? `[${this.namespace}]` : '';
      console.info(`✅ ${prefix} ${message}`, context || '');
    }
  }

  /**
   * Warn level - warning messages (always logged)
   */
  warn(message: string, context?: unknown): void {
    this.log('warn', message, context);
  }

  /**
   * Error level - error messages (always logged)
   */
  error(message: string, context?: unknown): void {
    this.log('error', message, context);
  }

  /**
   * Critical level - critical failures requiring immediate attention (always logged)
   */
  critical(message: string, context?: unknown): void {
    this.log('critical', message, context);
  }

  /**
   * Performance logging - logs if duration exceeds threshold
   */
  performance(message: string, durationMs: number, threshold = 100): void {
    if (durationMs > threshold) {
      this.warn(`Performance: ${message}`, {
        duration_ms: durationMs,
        threshold_ms: threshold,
        exceeded_by_ms: durationMs - threshold,
      });
    }
  }

  /**
   * Create a namespaced logger for a specific module
   */
  createNamespace(namespace: string): StructuredLogger {
    const fullNamespace = this.namespace
      ? `${this.namespace}:${namespace}`
      : namespace;
    return new StructuredLogger(fullNamespace);
  }
}

/**
 * Main logger instance
 */
export const logger = new StructuredLogger();

/**
 * Measure performance of an operation
 *
 * @param operationName - Name of the operation being measured
 * @param threshold - Threshold in ms to log (default 100ms)
 * @returns Function to call when operation completes
 *
 * @example
 * const measure = measurePerformance('fetchUsers');
 * await fetchUsers();
 * measure(); // Logs if duration > 100ms
 */
export function measurePerformance(
  operationName: string,
  threshold = 100
): () => void {
  const startTime = Date.now();

  return () => {
    const duration = Date.now() - startTime;
    logger.performance(operationName, duration, threshold);
  };
}

/**
 * Export default for convenience
 */
export default logger;
